import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Download, Clock, CheckCircle, AlertCircle, Printer, History, ArrowLeft, Code2 } from 'lucide-react';
import { AppLayout } from '../layouts/AppLayout';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const REPORT_ICONS = {
  'DEMONSTRATIVO_CONTROLE': FileText,
  'CERTIDAO_REGULARIDADE': CheckCircle,
  'MAPA_CONFORMIDADE': AlertCircle,
  'RELATORIO_GESTAO': FileText,
  'DECLARACAO_IDONEIDADE': CheckCircle,
  'ANALISE_RISCO_CONTRATACAO': AlertCircle,
};

/**
 * Renderiza recursivamente qualquer payload de relatório
 * de forma institucional (sem expor JSON cru ao usuário comum).
 */
function ReportPreviewBody({ data }) {
  if (data == null) return <p className="text-slate-500 italic">Sem dados para exibir.</p>;
  if (typeof data !== 'object') {
    return <p className="text-slate-700">{String(data)}</p>;
  }
  if (Array.isArray(data)) {
    if (data.length === 0) return <p className="text-slate-500 italic">Lista vazia.</p>;
    return (
      <div className="space-y-2">
        {data.map((item, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 bg-white">
            <ReportPreviewBody data={item} />
          </div>
        ))}
      </div>
    );
  }
  const entries = Object.entries(data);
  return (
    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
      {entries.map(([k, v]) => {
        const label = humanize(k);
        const isComplex = v !== null && typeof v === 'object';
        return (
          <div key={k} className={isComplex ? 'md:col-span-2' : ''}>
            <dt className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</dt>
            <dd className={isComplex ? '' : 'text-sm text-slate-800 break-words'}>
              {isComplex ? <ReportPreviewBody data={v} /> : formatScalar(v)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function humanize(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatScalar(v) {
  if (v == null || v === '') return <span className="text-slate-400">—</span>;
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try { return new Date(v).toLocaleString('pt-BR'); } catch { return v; }
  }
  return String(v);
}

export default function ReportsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({});
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [format, setFormat] = useState('pdf');
  const [previewData, setPreviewData] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);

  // Apenas perfis administrativos podem ver o JSON técnico
  const canSeeRawJson = ['ADMIN_ESTADUAL', 'CGE', 'CONTROLE_INTERNO', 'AUDITORIA'].includes(user?.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRes, historyRes] = await Promise.all([
        api.get('/reports/templates'),
        api.get('/reports/history?limit=10'),
      ]);
      setTemplates(templatesRes.data.templates || {});
      setHistory(historyRes.data.reports || []);
    } catch (error) {
      toast.error('Erro ao carregar templates de relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (templateId, outputFormat = 'json') => {
    try {
      setGenerating(templateId);
      
      let url = `/reports/generate/${templateId}?format=${outputFormat}`;
      if (startDate) url += `&start_date=${startDate}`;
      if (endDate) url += `&end_date=${endDate}`;
      
      if (outputFormat === 'pdf') {
        // Download PDF
        const response = await api.get(url, { responseType: 'blob' });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${templateId}_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        toast.success('Relatório PDF gerado com sucesso');
      } else {
        // Preview JSON
        const response = await api.get(url);
        setPreviewData(response.data);
        toast.success('Relatório gerado com sucesso');
      }
      
      loadData(); // Refresh history
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setGenerating(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="reports-back-btn"
            className="text-slate-600 hover:text-slate-900"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Printer className="h-6 w-6 text-slate-600" />
              Relatórios de Edital
            </h1>
            <p className="text-slate-500">Gere relatórios legais para processos licitatórios</p>
          </div>
        </div>
      </div>

      {/* Filtros de Período */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Período do Relatório</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div>
              <Label>Data Início</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Formato</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger className="w-[140px]" data-testid="report-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF (oficial)</SelectItem>
                  {canSeeRawJson && <SelectItem value="json">JSON (admin)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates de Relatórios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(templates).map(([id, template]) => {
          const Icon = REPORT_ICONS[id] || FileText;
          return (
            <Card key={id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <Icon className="h-8 w-8 text-blue-600" />
                  <Badge variant="outline" className="text-xs">{template.legal_basis}</Badge>
                </div>
                <CardTitle className="text-lg mt-2">{template.title}</CardTitle>
                <CardDescription className="text-sm">{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleGenerate(id, 'json')}
                    disabled={generating === id}
                    data-testid={`btn-generate-${id}-json`}
                  >
                    {generating === id ? (
                      <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-transparent rounded-full" />
                    ) : (
                      <>Visualizar</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => handleGenerate(id, 'pdf')}
                    disabled={generating === id}
                    data-testid={`btn-generate-${id}-pdf`}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview do Relatório - Formato Institucional */}
      {previewData && (
        <Card className="border-blue-200" data-testid="report-preview-card">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-slate-50 border-b border-blue-100 flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="text-xl text-slate-900">{previewData.template_title}</CardTitle>
              <CardDescription className="mt-1 space-y-0.5">
                <p>Gerado em <strong className="text-slate-700">{new Date(previewData.generated_at).toLocaleString('pt-BR')}</strong></p>
                <p>Base Legal: <Badge variant="outline" className="ml-1">{previewData.legal_basis}</Badge></p>
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {canSeeRawJson && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRawJson((v) => !v)}
                  data-testid="report-toggle-json"
                  title="Exportação técnica JSON (apenas admin)"
                >
                  <Code2 className="w-4 h-4 mr-1" />
                  {showRawJson ? 'Ocultar JSON' : 'JSON (admin)'}
                </Button>
              )}
              <Button
                size="sm"
                onClick={() => handleGenerate(previewData.template_id || selectedTemplate, 'pdf')}
                data-testid="report-export-pdf"
              >
                <Download className="w-4 h-4 mr-1" />
                Exportar PDF
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setPreviewData(null); setShowRawJson(false); }} data-testid="report-close-preview">
                Fechar
              </Button>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Preview formatado institucional */}
            <article className="prose max-w-none" data-testid="report-preview-content">
              <ReportPreviewBody data={previewData.data} />
            </article>

            {/* JSON cru apenas para admin e on-demand */}
            {canSeeRawJson && showRawJson && (
              <div className="mt-6 border-t pt-4" data-testid="report-raw-json">
                <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1">
                  <Code2 className="w-3 h-3" /> Exportação Técnica (JSON)
                </p>
                <div className="bg-slate-900 text-slate-100 rounded-lg p-4 overflow-auto max-h-96">
                  <pre className="text-xs whitespace-pre-wrap font-mono">
                    {JSON.stringify(previewData.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Histórico de Relatórios */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico de Relatórios Gerados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Template</TableHead>
                <TableHead>Formato</TableHead>
                <TableHead>Gerado por</TableHead>
                <TableHead>Data</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                    Nenhum relatório gerado ainda
                  </TableCell>
                </TableRow>
              ) : (
                history.map((report) => (
                  <TableRow key={report.report_id}>
                    <TableCell className="font-mono text-sm">{report.report_id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{report.template_title}</p>
                        <p className="text-xs text-slate-500">{report.legal_basis}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{report.format?.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{report.generated_by}</TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(report.generated_at).toLocaleString('pt-BR')}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
    </AppLayout>
  );
}
